import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Landmark, Loader2, Plus, Undo2 } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { BORDER, FormDrawer } from "@/components/shared/FormDrawer";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { formatDate } from "@/lib/utils";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { formatCurrency } from "@/modules/expenses/components/CurrencyAmount";
import {
  ACCOUNT_TYPE_LABELS,
  useAccountStatement,
  useLedgerAction,
  type StatementLine,
} from "../hooks/useFinanceAccounts";

const today = () => new Date().toISOString().slice(0, 10);

type Action =
  | { kind: "opening" }
  | { kind: "adjustment" }
  | { kind: "reverse"; line: StatementLine };

function EntryDrawer({
  action,
  accountId,
  currency,
  onClose,
}: {
  action: Action;
  accountId: string;
  currency: string;
  onClose: () => void;
}) {
  const post = useLedgerAction();
  const [entryDate, setEntryDate] = useState(today());
  const [direction, setDirection] = useState<"in" | "out">("in");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const title =
    action.kind === "opening"
      ? "Opening Balance"
      : action.kind === "adjustment"
        ? "New Adjustment"
        : `Reverse ${action.line.code}`;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const value = Number(amount);
    try {
      if (action.kind === "opening") {
        await post.mutateAsync({
          path: `${accountId}/opening-balance`,
          body: { entryDate, amount: direction === "out" ? -value : value, notes: reason || null },
        });
      } else if (action.kind === "adjustment") {
        await post.mutateAsync({
          path: `${accountId}/adjustments`,
          body: { entryDate, amount: direction === "out" ? -value : value, description, reason },
        });
      } else {
        await post.mutateAsync({
          path: `entries/${action.line.id}/reverse`,
          body: { entryDate, reason },
        });
      }
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <FormDrawer title={title} onClose={onClose} onSubmit={handleSubmit} error={error} busy={post.isPending}>
      {action.kind === "opening" && (
        <p className="text-xs text-muted-foreground">
          Money already in this account on the date you start recording. Enter it once; no entry can
          be dated before it. If the account started empty, you don't need one.
        </p>
      )}
      {action.kind === "reverse" && (
        <p className="text-xs text-muted-foreground">
          Posts an equal and opposite entry, so {action.line.code} ({action.line.description},{" "}
          {formatCurrency(action.line.moneyIn || -action.line.moneyOut, currency)}) no longer counts.
          Both stay on the statement.
        </p>
      )}

      <div className="space-y-2">
        <Label htmlFor="ent-date">
          Date <span className="text-destructive">*</span>
        </Label>
        <Input
          id="ent-date"
          type="date"
          value={entryDate}
          onChange={(e) => setEntryDate(e.target.value)}
          required
        />
      </div>

      {action.kind !== "reverse" && (
        <>
          {action.kind === "adjustment" && (
            <div className="space-y-2">
              <Label htmlFor="ent-desc">
                Description <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ent-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Bank charges for October"
                required
              />
            </div>
          )}
          <div className="grid grid-cols-[140px_1fr] gap-3">
            <div className="space-y-2">
              <Label htmlFor="ent-dir">Direction</Label>
              <Select
                id="ent-dir"
                value={direction}
                onChange={(e) => setDirection(e.target.value as "in" | "out")}
              >
                <option value="in">{action.kind === "opening" ? "In credit" : "Money in"}</option>
                <option value="out">{action.kind === "opening" ? "Overdrawn" : "Money out"}</option>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ent-amount">
                Amount ({currency}) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ent-amount"
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </div>
          </div>
        </>
      )}

      <div className="space-y-2">
        <Label htmlFor="ent-reason">
          {action.kind === "opening" ? "Notes" : "Reason"}
          {action.kind !== "opening" && <span className="text-destructive"> *</span>}
        </Label>
        <Input
          id="ent-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={action.kind === "reverse" ? "e.g. Entered the wrong amount" : undefined}
          required={action.kind !== "opening"}
        />
      </div>
    </FormDrawer>
  );
}

export default function AccountStatementPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { user } = useAuth();
  const permissions = user?.permissions?.finance_accounts;
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { data, isLoading, isError, error } = useAccountStatement(id, { from, to });
  const [action, setAction] = useState<Action | null>(null);

  const account = data?.account;
  const currency = account?.currency ?? "";

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Finance" },
        { label: "Accounts", href: "/finance/accounts" },
        { label: account?.name ?? "Statement" },
      ]}
    >
      <div className="w-full space-y-6">
        <Link
          to="/finance/accounts"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Accounts
        </Link>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            {(error as Error)?.message ?? "Failed to load statement"}
          </div>
        ) : isLoading || !data || !account ? (
          <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} />
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <Landmark className="h-[18px] w-[18px] text-foreground" strokeWidth={1.5} />
                  <h1 className="text-2xl font-semibold tracking-tight">{account.name}</h1>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {account.code} · {account.company.name} · {ACCOUNT_TYPE_LABELS[account.type]} ·{" "}
                  {currency}
                  {account.partner ? ` · held by ${account.partner.name}` : ""}
                  {account.isActive ? "" : " · Inactive"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {permissions?.canExport && (
                  <ExportButton
                    module="finance-accounts/statement"
                    filters={{ accountId: id, from: from || undefined, to: to || undefined }}
                  />
                )}
                {permissions?.canCreate && account.isActive && !data.hasOpeningBalance && (
                  <Button variant="outline" onClick={() => setAction({ kind: "opening" })}>
                    Opening balance
                  </Button>
                )}
                {permissions?.canCreate && account.isActive && (
                  <Button onClick={() => setAction({ kind: "adjustment" })}>
                    <Plus className="h-4 w-4" strokeWidth={1.5} />
                    Adjustment
                  </Button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label htmlFor="st-from" className="text-xs">From</Label>
                <Input id="st-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-44" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="st-to" className="text-xs">To</Label>
                <Input id="st-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-44" />
              </div>
              {(from || to) && (
                <Button variant="outline" size="sm" onClick={() => { setFrom(""); setTo(""); }}>
                  All dates
                </Button>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-4">
              {[
                ["Brought forward", data.openingBalance],
                ["Money in", data.totalIn],
                ["Money out", data.totalOut],
                ["Balance", data.closingBalance],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border bg-white p-4" style={BORDER}>
                  <div className="text-xs text-muted-foreground">{label}</div>
                  <div className="mt-1 text-lg font-semibold tabular-nums">
                    {formatCurrency(value as number, currency)}
                  </div>
                </div>
              ))}
            </div>

            <div className="overflow-hidden rounded-xl border bg-white" style={BORDER}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground" style={BORDER}>
                    <th className="px-4 py-3 font-medium">Date</th>
                    <th className="px-4 py-3 font-medium">Ref</th>
                    <th className="px-4 py-3 font-medium">Description</th>
                    <th className="px-4 py-3 text-right font-medium">In</th>
                    <th className="px-4 py-3 text-right font-medium">Out</th>
                    <th className="px-4 py-3 text-right font-medium">Balance</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {data.lines.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                        No entries {from || to ? "in this period" : "yet"}
                      </td>
                    </tr>
                  )}
                  {data.lines.map((l) => {
                    const struck = !!l.reversedByCode;
                    return (
                      <tr key={l.id} className="border-b last:border-0 align-top" style={BORDER}>
                        <td className="whitespace-nowrap px-4 py-3">{formatDate(l.entryDate)}</td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{l.code}</td>
                        <td className="px-4 py-3">
                          <div className={struck ? "text-muted-foreground line-through" : ""}>
                            {l.description}
                          </div>
                          {l.reason && (
                            <div className="text-xs text-muted-foreground">{l.reason}</div>
                          )}
                          {struck && (
                            <div className="text-xs text-muted-foreground">
                              Reversed by {l.reversedByCode}
                            </div>
                          )}
                          {l.sourceType === "finance_income" && (
                            <Link to="/finance/income" className="text-xs text-muted-foreground hover:underline">
                              From Income
                            </Link>
                          )}
                          {l.sourceType === "finance_contribution" && (
                            <Link to="/finance/contributions" className="text-xs text-muted-foreground hover:underline">
                              From Contributions
                            </Link>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {l.moneyIn ? formatCurrency(l.moneyIn, currency) : ""}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {l.moneyOut ? formatCurrency(l.moneyOut, currency) : ""}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {formatCurrency(l.balance, currency)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {permissions?.canEdit &&
                            !l.sourceType &&
                            !l.reversalOfCode &&
                            !l.reversedByCode && (
                              <button
                                type="button"
                                onClick={() => setAction({ kind: "reverse", line: l })}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600"
                                title="Reverse this entry"
                              >
                                <Undo2 className="h-[14px] w-[14px]" strokeWidth={1.5} />
                              </button>
                            )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {action && account && (
        <EntryDrawer
          action={action}
          accountId={id}
          currency={currency}
          onClose={() => setAction(null)}
        />
      )}
    </AppLayout>
  );
}
